import { Grid, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Core";
import { ChartJsOptions, DefaultListParameters } from "assets/appConfig";
import { DashboardPatterns, DashboardThemeClassKey } from "assets/jss/common-styles";
import * as ChartJs from "chart.js";
import { AppMessageBox } from "components/AppMessageBox";
import { SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import DashDoughnutListBox, { DashDoughnutBoxListItem } from "components/Dashboard/DashDoughnutListBox";
import IconKnowledgeSession from "components/Icons/IconKnowledgeSession";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiSelectFilter from "components/MuiExtras/MuiSelectFilter";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { KnowledgeTestDifficultyLevel } from "models/ApiEnums";
import { DashboardGraphByGroupModel, DashboardKnowledgeIndicatorsDataModel, DashboardKnowledgeIndicatorsResumeModel, DashboardKnowledgeIndicatorsSummaryColumn, DashboardUserEvolutionKnowledgeDetailColumn, PagedResult } from "models/ApiModels";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import { KnowledgeTestLevelName, KnowledgeTotalColumn, KnowlegeDataModelLine } from "views/Dashboards/components/KnowledgeCommons";
import { CommonProps } from "views/Dashboards/UserEvolutionDashboard";
import { getTopicSecondViewAction } from "views/Timeline/Components/FeedHelpers";
/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
}

type LocalProps = IProps & CommonProps;

/////////////////////////////////////////////////////////////////////////////////////

interface ICreateTileProps {
    activeTile: DashboardKnowledgeIndicatorsSummaryColumn;
    data: DashboardKnowledgeIndicatorsResumeModel;
    onChange: (tile: DashboardKnowledgeIndicatorsSummaryColumn) => void;
}

type CreateTileProps = ICreateTileProps & LocalProps;

class CreateTiles extends React.PureComponent<CreateTileProps>
{
    private handleTileClick(tile: DashboardKnowledgeIndicatorsSummaryColumn) {
        this.props.onChange(tile);
    }

    public render() {
        const { activeTile, data } = this.props;

        var boxes: DashDoughnutBoxListItem[] = [
            {
                key: DashboardKnowledgeIndicatorsSummaryColumn.Knowledge,
                title: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateTiles.Knowledge"),
                value: data.knowledge,
                nullText: "N/D"
            },
            {
                key: DashboardKnowledgeIndicatorsSummaryColumn.Result,
                title: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateTiles.Result"),
                value: data.result,
                nullText: "N/D"
            },
            {
                key: DashboardKnowledgeIndicatorsSummaryColumn.Completed,
                title: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateTiles.Completed"),
                value: data.completed,
                nullText: "N/D"
            },
            {
                key: DashboardKnowledgeIndicatorsSummaryColumn.Revision,
                title: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateTiles.Revision"),
                value: data.revision,
                nullText: "N/D"
            }
        ];

        return (
            <MuiContainer margin="normal">
                <DashDoughnutListBox
                    color="success"
                    boxes={boxes}
                    active={activeTile}
                    onClick={(key: DashboardKnowledgeIndicatorsSummaryColumn) => this.handleTileClick(key)}
                />
            </MuiContainer>
        );
    }
}


////////////////////////////////////////////////////////////////////////////////////////////////////

interface IGraphProps extends LocalProps {
    tile: DashboardKnowledgeIndicatorsSummaryColumn;
    onBarClick: (index: number) => void;
}

interface IGraphState {
    data: DashboardGraphByGroupModel;
    loading: boolean;
}

class CreateGraph extends React.PureComponent<IGraphProps, IGraphState>
{
    constructor(props: IGraphProps) {
        super(props);
        this.state = { data: null, loading: true };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph." + r);
    }

    ///
    private loadData() {

        api.getUserEvolutionKnowledgeAnalytic(
            {
                userId: this.props.userId,
                summaryColumn: this.props.tile,
                detailColumn: DashboardUserEvolutionKnowledgeDetailColumn.Graph
            })
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    private checkForUpdateState(prevProps: IGraphProps) {
        if (prevProps.tile != this.props.tile) {
            this.setState({ data: null, loading: true }, () => this.loadData());
        }
    }

    ///
    public componentDidUpdate(prevProps: IGraphProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public renderGraph() {
        const { onBarClick, tile } = this.props;
        const { data } = this.state;

        var ds: number[] = [];
        var labels: string[][] = [];
        var backgroundColors: string[] = [];

        var bc = DashboardPatterns.knowlegdeBars;

        switch (tile) {
            case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
                ds = [data.f0, data.f1, data.f2, data.f3, data.f4, data.f5];
                backgroundColors = [bc.f0.backgroundColor, bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
                labels = [[this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange0")], ["0-24%"], ["25-49%"], ["50-74%"], ["75-99%"], ["100%"]];
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Result:
                ds = [data.f1, data.f2, data.f3, data.f4];
                backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
                labels = [["0-24%"], ["25-49%"], ["50-74%"], ["75-100%"]];
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Completed:
                ds = [data.f1, data.f2, data.f3, data.f4, data.f5];
                backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
                labels = [["0-24%"], ["25-49%"], ["50-74%"], ["75-99%"], ["100%"]];
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Revision:
                ds = [data.f0, data.f1, data.f2, data.f3, data.f4];
                backgroundColors = [bc.f0.backgroundColor, bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
                labels = [
                    [this.res("RevisionRange0")],
                    [this.res("RevisionRange1")],
                    [this.res("RevisionRange2")],
                    [this.res("RevisionRange3")],
                    [this.res("RevisionRange4")]
                ];
                break;
        }

        const total = ds.length == 0 ? 0 : ds.reduce((p, c) => p + c);
        const d = ds.map((v, index) => { return { label: labels[index][0], backgroundColor: backgroundColors[index], data: [total == 0 ? 0 : v / total * 100.0] }; });

        return (
            <div id="graphCanvas" style={{ position: "relative", height: 150, width: "100%" }}>
                <Bar
                    data={
                        {
                            labels: [this.props.t("Views.Dashboards.Topic.KnowledgeDatabase.DistributionChartLabel")],
                            datasets: d
                        }
                    }
                    options={
                        {
                            ...ChartJsOptions,
                            indexAxis: "y",
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    display: true,
                                    position: "bottom"
                                },
                                tooltip: {
                                    callbacks: {
                                        label: function (item: any) {
                                            return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${ds[item.datasetIndex]})`;
                                        },
                                    }
                                }
                            },

                            scales: {
                                x: {
                                    display: true,
                                    stacked: true,
                                    title: {
                                        display: false,
                                    },
                                    min: 0,
                                    max: 100,
                                    grid: {
                                        display: false
                                    },
                                    ticks: {
                                        display: false
                                    }
                                },
                                y: {
                                    display: false,
                                    stacked: true,
                                    title: {
                                        display: false,
                                    }
                                }
                            }
                        }
                    }

                    getElementsAtEvent={
                        (e: Array<ChartJs.InteractionItem>, event: React.MouseEvent<HTMLCanvasElement>) => {
                            if (tile != DashboardKnowledgeIndicatorsSummaryColumn.Knowledge) {
                                if (e.length > 0) {
                                    const div = document.getElementById("graphCanvas").getBoundingClientRect();
                                    const bar = e.filter((p) => event.clientX >= (div.x + (p.element as any).base) && event.clientX <= (div.x + p.element.x));
                                    if (bar.length > 0) onBarClick(bar[0].datasetIndex);
                                }
                            }
                        }
                    }
                />
            </div>
        );
    }

    public render() {

        const { tile } = this.props;
        const { loading, data } = this.state;

        let title = "";
        let subtitle = "";

        switch (tile) {
            case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
                title = this.res("KnowledgeTitle");
                subtitle = this.res("KnowledgeSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Result:
                title = this.res("ResultTitle");
                subtitle = this.res("ResultSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Completed:
                title = this.res("CompleteTitle");
                subtitle = this.res("CompleteSubtitle");
                break;
            case DashboardKnowledgeIndicatorsSummaryColumn.Revision:
                title = this.res("RevisionTitle");
                subtitle = this.res("RevisionSubtitle");
                break;
        }

        return (
            <MuiContainer>
                <MuiContainer margin="dense" >
                    <Typography component="p" variant="h6"><b>{title.toString().toLocaleUpperCase()}</b></Typography>
                    <MuiMuted><small>{subtitle}</small></MuiMuted>
                </MuiContainer>

                {
                    loading
                        ? <SpinnerIndicator />
                        : data == null
                            ? <AppMessageBox resource="LoadGraphDataFailed" />
                            : this.renderGraph()
                }
            </MuiContainer>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////

interface IListProps extends LocalProps {
    tile: DashboardKnowledgeIndicatorsSummaryColumn;
    selectedIndex?: number;
}

interface CreateListState {
    dialog: any;
    data: PagedResult<DashboardKnowledgeIndicatorsDataModel>;
    offset: number;
    loading: boolean;
    creatingNewTopic: boolean;
    creatingNewTopicFailed: React.ReactNode;
    primaryListOption: DashboardUserEvolutionKnowledgeDetailColumn;
    secondaryListOption: number;
}

type CreateListProps = IListProps & RouteComponentProps;

class CreateList extends React.PureComponent<CreateListProps, CreateListState> {

    constructor(props: CreateListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            creatingNewTopic: false,
            creatingNewTopicFailed: null,
            primaryListOption: DashboardUserEvolutionKnowledgeDetailColumn.Better,
            secondaryListOption: null
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph." + r);
    }

    ///
    private res2(r: string) {
        return this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateList." + r);
    }

    ///
    private primaryList = [
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Knowledge,
            options: [
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Better, text: this.res2("KnowledgeOp1") },
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Worst, text: this.res2("KnowledgeOp2") },
            ],
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Result,
            options: [
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Better, text: this.res2("ResultOp1") },
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Worst, text: this.res2("ResultOp2") },
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Selected, text: this.res2("ResultOp3") }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Completed,
            options: [
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Better, text: this.res2("CompletedOp1") },
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Worst, text: this.res2("CompletedOp2") },
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Selected, text: this.res2("CompletedOp3") }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Revision,
            options: [
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Better, text: this.res2("RevisionOp1") },
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Worst, text: this.res2("RevisionOp2") },
                { key: DashboardUserEvolutionKnowledgeDetailColumn.Selected, text: this.res2("RevisionOp3") }
            ]
        }
    ];

    ///
    private secondayList = [
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Result,
            options: [
                { key: 0, text: "0-24%" },
                { key: 1, text: "25-49%" },
                { key: 2, text: "50-74%" },
                { key: 3, text: "75-100%" }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Completed,
            options: [
                { key: 0, text: "0-24%" },
                { key: 1, text: "25-49%" },
                { key: 2, text: "50-74%" },
                { key: 3, text: "75-99%" },
                { key: 4, text: "100%" }
            ]
        },
        {
            column: DashboardKnowledgeIndicatorsSummaryColumn.Revision,
            options: [
                { key: 0, text: this.res("RevisionRange0") },
                { key: 1, text: this.res("RevisionRange1") },
                { key: 2, text: this.res("RevisionRange2") },
                { key: 3, text: this.res("RevisionRange3") },
                { key: 4, text: this.res("RevisionRange4") }
            ]
        }
    ];

    ///
    private loadData(offset: number) {

        this.setState({ loading: true });

        api.getUserEvolutionKnowledgeAnalytic(
            {
                userId: this.props.userId,
                summaryColumn: this.props.tile,
                detailColumn: this.state.primaryListOption,
                selectedValue: this.state.secondaryListOption,
                range: { offset, take: DefaultListParameters.viewRecordsByPage }
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }


    ///
    private getIcons(data: DashboardKnowledgeIndicatorsDataModel): React.ReactNode {

        switch (this.props.tile) {
            case DashboardKnowledgeIndicatorsSummaryColumn.Knowledge:
            case DashboardKnowledgeIndicatorsSummaryColumn.Result:
                return (
                    <>
                        {
                            data.totalUnanswered + data.totalWrong > 0 &&
                            <MuiWarning>
                                <MuiClickable onClick={() => this.createNewTopicSession(data.topicId)}>
                                    <IconKnowledgeSession title={this.res2("NewTestSession")} />
                                </MuiClickable>
                            </MuiWarning>
                        }
                    </>
                );
        }

        return null;
    }

    ///
    private createNewTopicSession(topicId: string) {

        this.setState({ creatingNewTopic: true });

        api.quizCreateTopicSession(this.props.userId, topicId)
            .then((sessionId) => {
                if (sessionId == null) {
                    this.setState({
                        creatingNewTopic: false,
                        creatingNewTopicFailed: <MuiMessageBox message={this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateTiles.Completed")} variant="error" />
                    });
                }
                else {
                    this.props.history.push(RoutePaths.QuizExecute.replace(":id", sessionId));
                }
            })
            .catch((error) => {
                this.setState({
                    creatingNewTopic: false,
                    creatingNewTopicFailed: <RequestErrorReport error={error} />
                });
            });
    }

    ///
    private handleListOption = (e: SelectChangeEvent<DashboardUserEvolutionKnowledgeDetailColumn>) => {
        this.setState({
            primaryListOption: e.target.value as DashboardUserEvolutionKnowledgeDetailColumn,
            secondaryListOption: e.target.value == DashboardUserEvolutionKnowledgeDetailColumn.Selected
                ? this.secondayList.find(p => p.column == this.props.tile).options[0].key
                : null
        }, () => this.loadData(0));
    };

    ///
    private handleSecondaryListOption = (e: SelectChangeEvent<number>) => {
        this.setState({ secondaryListOption: e.target.value as number }, () => this.loadData(0));
    };

    ///
    private getPrimaryListOptions() {
        var filter = this.primaryList.find(p => p.column == this.props.tile);
        return filter.options;
    }

    ///
    private getSecondaryListOptions() {
        var filter = this.secondayList.find(p => p.column == this.props.tile);
        return filter?.options;
    }

    ///
    private renderHeader() {
        return (
            <div id="divList" >
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>

                            <Select
                                value={this.state.primaryListOption}
                                onChange={this.handleListOption}
                            >
                                {
                                    this.getPrimaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                }
                            </Select>

                        </Grid>

                        {
                            this.props.selectedIndex != null && this.state.primaryListOption == DashboardUserEvolutionKnowledgeDetailColumn.Selected &&
                            <Grid item>
                                <MuiSelectFilter
                                    value={this.state.secondaryListOption}
                                    onChange={this.handleSecondaryListOption}
                                >
                                    {
                                        this.getSecondaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                    }
                                </MuiSelectFilter>
                            </Grid>
                        }
                    </Grid>
                </MuiContainer>
            </div>

        );
    }

    ///
    private checkForUpdateState(prevProps: CreateListProps) {
        if (this.props.tile != prevProps.tile || (this.props.selectedIndex != prevProps.selectedIndex && this.props.selectedIndex == null)) {
            this.setState({
                creatingNewTopic: false,
                creatingNewTopicFailed: null,
                data: null,
                loading: true,
                dialog: null,
                offset: 0,
                primaryListOption: this.primaryList.find(p => p.column == this.props.tile).options[0].key,
                secondaryListOption: null
            }, () => this.loadData(0));
        }
        else if (this.props.selectedIndex != prevProps.selectedIndex) {

            const pFilter = this.primaryList.find(p => p.column == this.props.tile);

            this.setState({
                creatingNewTopic: false,
                creatingNewTopicFailed: null,
                data: null,
                loading: true,
                dialog: null,
                offset: 0,
                primaryListOption: pFilter.options[pFilter.options.length - 1].key,
                secondaryListOption: this.props.selectedIndex
            }, () => this.loadData(0));
        }
    }

    ///
    public componentDidUpdate(prevProps: CreateListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {

        const { data, loading, dialog, secondaryListOption, offset } = this.state;

        return (
            <MuiContainer margin="normal">
                <>
                    {dialog}
                    <br />
                    {this.renderHeader()}

                    {
                        loading
                            ? <SpinnerIndicator />
                            : data == null
                                ? <AppMessageBox resource="LoadListDataFailed" />
                                : secondaryListOption == null && data.total == 0
                                    ? <MuiMessageBox variant="info" message={this.res2(this.props.tile == DashboardKnowledgeIndicatorsSummaryColumn.Revision ? "RevisionEmptyData" : "EmptyData")} />
                                    : secondaryListOption != null && data.total == 0
                                        ? <MuiMessageBox variant="info" message={this.res2("FilteredDataEmpty")} />
                                        : (
                                            <>
                                                <MuiContainer>
                                                    <Table size="small">
                                                        <TableBody>
                                                            {
                                                                data.items.map((v, index) => {

                                                                    return (
                                                                        <TableRow key={index}>
                                                                            <TableCell style={{ width: 1, verticalAlign: "top" }} valign="top">{offset + index + 1}</TableCell>
                                                                            <TableCell style={{ width: 1 }}>
                                                                                <img src={v.thumbnailUrl} alt={v.name} style={{ maxWidth: 64 }} />
                                                                            </TableCell>
                                                                            <TableCell style={{ verticalAlign: "top" }}>
                                                                                <Grid container spacing={1}>
                                                                                    <Grid item>
                                                                                        <MuiLink color="secondary" onClick={() => getTopicSecondViewAction(v.topicId)()} target="_blank">
                                                                                            {v.name}
                                                                                        </MuiLink>
                                                                                        <MuiMuted>
                                                                                            <small>
                                                                                                {
                                                                                                    v.currentLevel == KnowledgeTestDifficultyLevel.NotInitiate
                                                                                                        ? <div><KnowledgeTestLevelName value={v.currentLevel} /></div>
                                                                                                        : (
                                                                                                            <div>
                                                                                                                <KnowlegeDataModelLine summaryColumn={this.props.tile} data={v} />
                                                                                                            </div>
                                                                                                        )
                                                                                                }
                                                                                            </small>
                                                                                        </MuiMuted>
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </TableCell>
                                                                            <TableCell align="right" style={{ verticalAlign: "top" }}>
                                                                                <KnowledgeTotalColumn summaryColumn={this.props.tile} data={v} />
                                                                                <br />
                                                                                {this.getIcons(v)}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })
                                                            }
                                                        </TableBody>
                                                    </Table>
                                                </MuiContainer>
                                                <MuiPagination
                                                    limit={DefaultListParameters.viewRecordsByPage}
                                                    offset={this.state.offset}
                                                    total={data.total}
                                                    onClick={(e, offset) => this.loadData(offset)}
                                                />
                                            </>
                                        )
                    }
                </>
            </MuiContainer>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////

interface UserEvolutionPublicationState {
    data: DashboardKnowledgeIndicatorsResumeModel;
    loading: boolean;
    selectedIndex?: number;
    tile: DashboardKnowledgeIndicatorsSummaryColumn;
}

type UserEvolutionKnowledgeProps = LocalProps & StyledComponentProps<DashboardThemeClassKey> & RouteComponentProps;

class UserEvolutionKnowledge extends React.PureComponent<UserEvolutionKnowledgeProps, UserEvolutionPublicationState> {

    constructor(props: UserEvolutionKnowledgeProps) {
        super(props);
        this.state = {
            data: null,
            loading: true,
            tile: DashboardKnowledgeIndicatorsSummaryColumn.Knowledge,
        };
    }

    private loadData() {

        api.getUserEvolutionKnowledgeResume(this.props.userId)
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        const { tile, data, loading } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        if (data == null)
            return <AppMessageBox resource="LoadPageDataFailed" />;

        return (
            <>
                <CreateTiles
                    {...this.props}
                    data={data}
                    activeTile={this.state.tile}
                    onChange={(tile) => this.setState({ tile: tile, selectedIndex: null })}
                />

                <MuiContainer margin="normal">
                    <MuiPaper borderVariant="accentedRound" borderColor="success">

                        <MuiContainer margin="normal">
                            <CreateGraph
                                {...this.props}
                                tile={tile}
                                onBarClick={
                                    (index: number) => {
                                        this.setState({ selectedIndex: index });
                                    }
                                }
                            />
                        </MuiContainer>

                        <CreateList
                            {...this.props}
                            tile={tile}
                            selectedIndex={this.state.selectedIndex}
                        />

                    </MuiPaper>
                </MuiContainer>

            </>
        );
    }
}

export default withTranslation()(withRouter(UserEvolutionKnowledge));